import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  X,
  Briefcase
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import useToast from "../hooks/useToast";
import {
  createCareer,
  deleteCareer,
  getCareers,
  updateCareer
} from "../services/careerService";

const careerSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  type: z.string().trim().optional(),
  department: z.string().trim().optional(),
  description: z.string().trim().optional()
});

const defaultValues = {
  title: "",
  type: "Full Time",
  department: "",
  description: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Career() {
  const [careers, setCareers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingCareer, setIsDeletingCareer] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCareerId, setEditingCareerId] = useState(null);
  const [careerPendingDelete, setCareerPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(careerSchema)
  });

  const descriptionContent = watch("description");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadCareers(targetPage, targetLimit);
  };

  async function loadCareers(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getCareers({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setCareers(response.data.careers || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load careers");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadCareers(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingCareerId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingCareerId(null);
    setIsModalOpen(false);
  };

  const openDeleteModal = (careerItem) => {
    setCareerPendingDelete(careerItem);
  };

  const closeDeleteModal = () => {
    if (isDeletingCareer) return;
    setCareerPendingDelete(null);
  };

  const handleEdit = (careerItem) => {
    setEditingCareerId(careerItem.id);
    reset({
      title: careerItem.title,
      type: careerItem.type || "Full Time",
      department: careerItem.department || "",
      description: careerItem.description || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!careerPendingDelete) return;
    setIsDeletingCareer(true);
    const response = await deleteCareer(careerPendingDelete.id);
    setIsDeletingCareer(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete career");
      return;
    }

    const nextPage = careers.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Career deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const response = editingCareerId
      ? await updateCareer(editingCareerId, values)
      : await createCareer(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save career");
      return;
    }

    showSuccessToast(response.message || "Career saved successfully");
    closeModal();
    if (editingCareerId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Careers Module"
        title="Careers"
        description="Manage job postings and career opportunities."
        badge={`${pagination.total} Careers`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Job Postings</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">View and manage entries with paginated control.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" /> Add Career
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border p-6 text-sm text-panel-muted">Loading careers...</div>
          ) : careers.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Title</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Type</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Department</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {careers.map((item) => (
                        <tr key={item.id} className="border-t border-slate-100 align-top">
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{item.title}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{item.type || "--"}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{item.department || "--"}</td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(item)}>
                                <Pencil className="mr-2 h-4 w-4" /> Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => openDeleteModal(item)}>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> careers
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => setPagination((c) => ({ ...c, page: c.page - 1 }))}>Prev</Button>
                  {pageNumbers.map((p, i) => (
                    p === "..." ? <span key={i} className="px-1 text-sm">...</span> : 
                    <Button key={i} type="button" size="sm" variant={pagination.page === p ? "default" : "outline"} onClick={() => setPagination((c) => ({ ...c, page: p }))}>{p}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination((c) => ({ ...c, page: c.page + 1 }))}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-8 text-center">No careers found.</div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[90dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border bg-white shadow-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h2 className="text-2xl font-bold">{editingCareerId ? "Edit Career" : "Add Career"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <FieldLabel>Title *</FieldLabel>
                <Input placeholder="Enter job title" {...register("title")} />
                {errors.title && <FieldError>{errors.title.message}</FieldError>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <FieldLabel>Type</FieldLabel>
                  <Select {...register("type")}>
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                  </Select>
                </div>
                <div className="space-y-2">
                  <FieldLabel>Department</FieldLabel>
                  <Input placeholder="Enter department" {...register("department")} />
                </div>
              </div>

              <div className="space-y-2 career-editor-wrapper">
                <style>{`
                  .career-editor-wrapper .ql-container { height: 200px; overflow-y: auto; }
                  .career-editor-wrapper .ql-editor { min-height: 200px; }
                `}</style>
                <FieldLabel>Description (Rich Text)</FieldLabel>
                <ReactQuill theme="snow" value={descriptionContent || ""} onChange={(content) => setValue("description", content)} />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : editingCareerId ? "Update Career" : "Save Career"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {careerPendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold">Delete Career</h2>
            <p className="mt-3 text-sm text-panel-muted">
              Are you sure you want to delete <span className="font-semibold">{careerPendingDelete.title}</span>?
            </p>
            <div className="mt-6 flex gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeletingCareer}>
                {isDeletingCareer ? "Deleting..." : "Delete Career"}
              </Button>
              <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={isDeletingCareer}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
